import _ from "lodash";

import { IGenericModelFields } from "../IGenericFields";
import { WorkspaceResourceType } from "./IWorkspaceResource";
import { WorkspaceUserRole } from "./IWorkspace";
import { IWorkspacePermissionAccess } from "./IWorkspacePermission";

export enum WorkspaceResourceFolderType {
    /**
     * What used to be team folders
     */
    SHARED = "shared",
    /**
     * What used to be all team members folder
     */
    SHARED_DEFAULT = "sharedDefault"
}

export interface IWorkspaceResourceFolder extends IGenericModelFields {
    readonly workspaceId: string;
    readonly resourceTypes: WorkspaceResourceType[];
    /**
     * @deprecated Superseded by `resourceTypes`. Still written during the transition so that folders
     * stay usable by API versions pinned to older client releases, and normalized into
     * `resourceTypes` by the model's `toObject` transform. Remove it (schema, dual-write and
     * transform) once those versions are retired.
     */
    readonly resourcesType?: WorkspaceResourceType;
    folderType: WorkspaceResourceFolderType;
    name: string;
    userGroupId: string;
    permissionsOverrides?: {
        [key in WorkspaceUserRole]?: IWorkspacePermissionAccess;
    }
    parentFolderId?: string;
}

export const ALL_TEAM_MEMBERS_NAME = "All Team Members";
export const ALL_TEAM_MEMBERS_FOLDER_NAME = ALL_TEAM_MEMBERS_NAME;

/**
 * Team resource folder types (librarian implicit access, custom shared folders).
 * Slide templates share the SLIDE "All Team Members" default — no separate ATM.
 */
export const TEAM_RESOURCE_FOLDER_TYPES: WorkspaceResourceType[] = [
    WorkspaceResourceType.SLIDE,
    WorkspaceResourceType.SLIDE_TEMPLATE,
    WorkspaceResourceType.PRESENTATION_TEMPLATE,
    WorkspaceResourceType.ASSET,
];

/**
 * A slides folder holds shared slides and slide templates alike.
 * SLIDE stays first (matches migrate-folder-resource-types).
 */
export const SLIDE_FOLDER_RESOURCE_TYPES: WorkspaceResourceType[] = [
    WorkspaceResourceType.SLIDE,
    WorkspaceResourceType.SLIDE_TEMPLATE,
];

export function isTeamResourceFolderType(resourceType: WorkspaceResourceType): boolean {
    return TEAM_RESOURCE_FOLDER_TYPES.includes(resourceType);
}

/** Post migrate-folder-resource-types: membership is `resourceTypes` only. */
export function folderMatchesResourceTypes(
    folder: Pick<IWorkspaceResourceFolder, "resourceTypes">,
    resourceTypes: readonly WorkspaceResourceType[]
): boolean {
    return (folder.resourceTypes ?? []).some(type => resourceTypes.includes(type));
}

/**
 * Presentation ATM folders use SHARED_DEFAULT.
 * Team-resource ATM folders (slides/assets/templates) use SHARED + the ATM name. but we only check for SHARED_DEFAULT.
 */
export function isAllTeamMembersFolder(folder: Pick<IWorkspaceResourceFolder, "folderType">) {
    return folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT;
}

/**
 * Slides, templates, and assets ATM (SHARED + name).
 * Delete stays blocked until the main release recreates a missing folder and backfills resources.
 * Remove this with that change.
 */
export function isTeamResourceAllTeamMembersFolder(
    folder: Pick<IWorkspaceResourceFolder, "folderType" | "name" | "resourceTypes">
) {
    return folder.folderType === WorkspaceResourceFolderType.SHARED &&
        folder.name === ALL_TEAM_MEMBERS_FOLDER_NAME &&
        folderMatchesResourceTypes(folder, TEAM_RESOURCE_FOLDER_TYPES);
}

/** All Team Members, a direct subfolder, or no folder. Subfolders are only one level deep. No folder counts so callers can negate once. */
export function isTeamResourceAllTeamMembersFolderOrSubfolder(
    folder?: Pick<IWorkspaceResourceFolder, "folderType" | "name" | "resourceTypes"> | null,
    parentFolder?: Pick<IWorkspaceResourceFolder, "folderType" | "name" | "resourceTypes"> | null
) {
    if (!folder) {
        return true;
    }

    return isTeamResourceAllTeamMembersFolder(folder) ||
        (!!parentFolder && isTeamResourceAllTeamMembersFolder(parentFolder));
}

/**
 * Guests cannot create presentation templates — exclude them from presentation-template folder invites.
 * Presentation, asset, and slide folders still allow guest invites.
 */
export function shouldExcludeGuestsFromFolderAccess(
    folder: Pick<IWorkspaceResourceFolder, "resourceTypes">
) {
    return (folder.resourceTypes ?? []).includes(WorkspaceResourceType.PRESENTATION_TEMPLATE);
}

/** True when members cannot manage folder permissions (owner-only edit). */
export function isFolderOnlyOwnerCanEdit(
    folder: Pick<IWorkspaceResourceFolder, "permissionsOverrides">
) {
    return !folder.permissionsOverrides?.[WorkspaceUserRole.MEMBER]?.write?.permissions;
}

export interface IWorkspaceResourceFolderMetadataCounters {
    users: {
        [key in WorkspaceUserRole]: number;
    },
    resources: {
        [key in WorkspaceResourceType]: number;
    }
}

export interface IWorkspaceResourceFolderMetadata {
    id: string;
    name: string;
    counters: IWorkspaceResourceFolderMetadataCounters;
}

////////////////////////////// DEFAULTS //////////////////////////////

const DEFAULT_METADATA_COUNTERS: IWorkspaceResourceFolderMetadataCounters = {
    users: Object.values(WorkspaceUserRole).reduce((acc, role) => ({ ...acc, [role]: 0 }), {} as { [key in WorkspaceUserRole]: number }),
    resources: Object.values(WorkspaceResourceType).reduce((acc, resourceType) => ({ ...acc, [resourceType]: 0 }), {} as { [key in WorkspaceResourceType]: number }),
};

export const GET_DEFAULT_WORKSPACE_RESOURCE_FOLDER_METADATA_COUNTERS = (): IWorkspaceResourceFolderMetadataCounters => _.cloneDeep(DEFAULT_METADATA_COUNTERS);

export function GET_DEFAULT_WORKSPACE_RESOURCE_FOLDER_PERMISSIONS_OVERRIDES(folderType: WorkspaceResourceFolderType): IWorkspaceResourceFolder["permissionsOverrides"] {
    if (folderType === WorkspaceResourceFolderType.SHARED_DEFAULT) {
        return {
            [WorkspaceUserRole.OWNER]: {
                read: {
                    object: true,
                    permissions: true
                },
                write: {
                    object: true,
                    permissions: true
                },
                owner: true
            },
            [WorkspaceUserRole.LIBRARIAN]: {
                read: {
                    object: true,
                    permissions: true
                },
                write: {
                    object: true,
                    permissions: false
                },
                owner: false
            },
            [WorkspaceUserRole.MEMBER]: {
                read: {
                    object: true,
                    permissions: true,
                },
                write: {
                    object: true,
                    permissions: false,
                },
                owner: false
            },
            [WorkspaceUserRole.GUEST]: {
                read: {
                    object: true,
                    permissions: true
                },
                write: {
                    object: false,
                    permissions: false
                },
                owner: false
            }
        };
    }

    return {
        [WorkspaceUserRole.OWNER]: {
            read: {
                object: true,
                permissions: true
            },
            write: {
                object: true,
                permissions: true
            },
            owner: true
        },
        [WorkspaceUserRole.LIBRARIAN]: {
            read: {
                object: true,
                permissions: true
            },
            write: {
                object: true,
                permissions: true
            },
            owner: false
        },
        [WorkspaceUserRole.MEMBER]: {
            read: {
                object: true,
                permissions: true
            },
            write: {
                object: true,
                permissions: true
            },
            owner: false
        },
        [WorkspaceUserRole.GUEST]: {
            read: {
                object: true,
                permissions: true
            },
            write: {
                object: false,
                permissions: false
            },
            owner: false
        }
    };
}

// Team resource folder overrides (members/guests read-only; owners/librarians edit).
export function GET_DEFAULT_TEAM_RESOURCE_FOLDER_PERMISSIONS_OVERRIDES(): IWorkspaceResourceFolder["permissionsOverrides"] {
    return {
        [WorkspaceUserRole.OWNER]: {
            read: { object: true, permissions: true },
            write: { object: true, permissions: true },
            owner: true
        },
        [WorkspaceUserRole.LIBRARIAN]: {
            read: { object: true, permissions: true },
            write: { object: true, permissions: true },
            owner: false
        },
        [WorkspaceUserRole.MEMBER]: {
            read: { object: true, permissions: true },
            write: { object: false, permissions: false },
            owner: false
        },
        [WorkspaceUserRole.GUEST]: {
            read: { object: true, permissions: true },
            write: { object: false, permissions: false },
            owner: false
        }
    };
}

export function GET_FOLDER_PERMISSIONS_OVERRIDES_FOR_RESOURCES_TYPE(
    folderType: WorkspaceResourceFolderType,
    resourceType: WorkspaceResourceType
): IWorkspaceResourceFolder["permissionsOverrides"] {
    if (isTeamResourceFolderType(resourceType)) {
        return GET_DEFAULT_TEAM_RESOURCE_FOLDER_PERMISSIONS_OVERRIDES();
    }
    return GET_DEFAULT_WORKSPACE_RESOURCE_FOLDER_PERMISSIONS_OVERRIDES(folderType);
}
